import { useState, useMemo, useEffect } from "react";
import { User, Settings as SettingsIcon, Timer } from "lucide-react";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Device } from "../../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../../StorageHelper";
import {
  getPrivilegeMgmtForProfile,
  getPrivilegeMgmtForUsers,
  updatePriviledgeForProfiles,
  updatePriviledgeForUsers,
} from "../Api/resourceApi";
import { ProfilePrivilege } from "../resourceInterface";
import useUserStore from "../../../../services/userStore";
import { debounce } from "../../../../services/Helpers";
import { getInheritedEndpointResourceStatusValue, getInheritedEndpointResourceStatusValueForFolders, getInheritedEndpointResourceStatusValueForProfilePrivilege, labelMapping, mapUserGroupsToProfilePrivileges } from "../helper";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { Skeleton } from "../../interfaces/CommonInterfaces";

export interface PrivilegeManagementTabProps {
  selectedUser: string | null;
  onUserSelect: (userId: string | null) => void;
  users: any[];
  device: Device;
  profileId: number;
  os: string;
}

type Status = "Allowed" | "Denied" | "Timed" | "Inherited";

export default function PrivilegeManagementTab({
  selectedUser,
  onUserSelect,
  users,
  device,
  profileId,
  os,
}: PrivilegeManagementTabProps) {
  const [selectedPrivilege, setSelectedPrivilege] = useState<ProfilePrivilege | null>(null);
  const [privilegeSearch, setPrivilegeSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [priviledges, setPriviledges] = useState<ProfilePrivilege[]>([]);
  const [priviledgeFilter, setPriviledgeFilter] = useState("ALL");
  const { email, role } = useUserStore((state) => state.user);
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const [loading, setLoading] = useState(false);

  // Timer data (example)
  const [timerData, setTimerData] = useState<Record<string, { remainingMinutes: number; totalMinutes: number }>>({});

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter((user) => user.cn.toLowerCase().includes(userSearch.toLowerCase()));
  }, [users, userSearch]);

  const fetchPrivileges = async (isFirst = true) => {
    if (!tenantName) return;
    setLoading(isFirst);
    try {
      if (selectedUser && selectedUser !== "system") {
        const response = await getPrivilegeMgmtForUsers(selectedUser, tenantName);
        setPriviledges(mapUserGroupsToProfilePrivileges(response));
        const timers: Record<
          number,
          { remainingMinutes: number; totalMinutes: number }
        > = {};
        response.forEach((item) => {
          const remainingSeconds = Number(item.remainingTime);
          if (Number.isFinite(remainingSeconds) && remainingSeconds > 0) {
            const remainingMinutes = Math.ceil(remainingSeconds / 60);
            const totalMinutes = remainingMinutes;
            timers[item.adComputerUserGroup.cn] = { remainingMinutes, totalMinutes };
          }
        });
        setTimerData(timers);
      } else {
        const response = await getPrivilegeMgmtForProfile(profileId, os);
        setPriviledges(response);
      }
    } catch (err) {
      console.error("Failed to fetch privileges:", err);
      setTimerData({});
      setPriviledges([])
    }
    setLoading(false);
  };

  useEffect(() => {

    fetchPrivileges();
    setSelectedPrivilege(null);

  }, [selectedUser, tenantName, profileId, os, users]);

  const filteredPrivileges = useMemo(() => {
    return priviledges.filter((p) => p.cn.toLowerCase().includes(privilegeSearch.toLowerCase()));
  }, [priviledges, privilegeSearch]);


  const handleUserSearch = debounce((value: string) => setUserSearch(value), 300);

  const handlePrivilegeStatusChange = async (groupId: number, profileId: number, newStatus: string) => {
    if (!selectedUser) return;
    const normalizedStatus = newStatus === "BLOCKED" ? "DISABLED" : newStatus;
    const timeInSeconds = (device?.defaultTime || 0) * 60;

    if (selectedUser === "system") {
      await updatePriviledgeForProfiles(email, groupId, normalizedStatus, profileId, tenantName);
    } else {
      await updatePriviledgeForUsers(email, groupId, normalizedStatus, tenantName, device.id, role, selectedUser, timeInSeconds);

    }
    await fetchPrivileges(false);
  };

  const getPrivilegeStatus = (privilege: ProfilePrivilege) => {
    if (!selectedUser) return privilege.profileResource === "ALLOWED" ? "ENABLED" : privilege.profileResource;
    return privilege.profileResource === "ALLOWED" ? "ENABLED" : privilege.profileResource;
  };

  const getStatus = (privilege: ProfilePrivilege) => {
    return labelMapping(getPrivilegeStatus(privilege), getInheritedEndpointResourceStatusValueForProfilePrivilege(privilege))
  }

  const getPrivilegeSelectStatusValue = (privilege: ProfilePrivilege | null | undefined) => {
    if (!privilege) return undefined;
    const status = getPrivilegeStatus(privilege);
    if (!status) return undefined;

    const normalizedStatus = String(status).toUpperCase();
    if (normalizedStatus === "INHERITED" && selectedUser === "system") {
      const inheritedStatus = String(
        privilege.inheritedGroupStatus || privilege.profileResource || ""
      ).toUpperCase();

      if (inheritedStatus === "BLOCKED" || inheritedStatus === "DENIED") return "DISABLED";
      if (inheritedStatus === "ALLOWED") return "ENABLED";
      if (inheritedStatus === "TIMED") return "DYNAMIC";
      if (inheritedStatus === "DISABLED" || inheritedStatus === "DYNAMIC" || inheritedStatus === "ENABLED") return inheritedStatus;
      return "ENABLED";
    }
    if (normalizedStatus === "BLOCKED") return "DISABLED";
    if (normalizedStatus === "ALLOWED") return "ENABLED";
    if (normalizedStatus === "TIMED") return "DYNAMIC";
    return normalizedStatus;
  }

  const getTimerDisplay = (privilegeId: string) => {
    const timer = timerData[privilegeId];
    if (!timer) return "";
    const remaining = Number(timer.remainingMinutes);
    if (!Number.isFinite(remaining) || remaining <= 0) return "0m";
    const hours = Math.floor(remaining / 60);
    const mins = remaining % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimerData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((id) => {
          if (updated[id].remainingMinutes > 0) updated[id].remainingMinutes -= 1;
        });
        return updated;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const filteredURL = useMemo(() => {
    const filterMap: Record<string, string[]> = {
      ALL: ["ALLOWED", "DENIED", "TIMED", "INHERITED"],
      ALLOWED: ["ALLOWED"],
      DENIED: ["DENIED"],
      TIMED: ["TIMED"],
    };
    return filteredPrivileges.filter((app) => {
      const appData = app?.cn;
      if (!appData) return false;
      const status = labelMapping(getPrivilegeStatus(app), getInheritedEndpointResourceStatusValueForProfilePrivilege(app));
      const matchesPrivilege = filterMap[priviledgeFilter]?.includes(status) ?? true;
      const matchesSearch = appData
        ?.toLowerCase()
        .includes(privilegeSearch.toLowerCase());

      return matchesPrivilege && matchesSearch;
    });
  }, [filteredPrivileges, privilegeSearch, selectedUser, priviledgeFilter]);

  useEffect(() => {
    setSelectedPrivilege(null);
  }, [priviledgeFilter])

  return (
    <div className="flex flex-1 overflow-hidden">
      {selectedUser !== "system" && (
        <div className="w-64 p-4 bg-white border-r dark:bg-background">
          <h3 className="mb-4 text-sm font-medium">Users List</h3>
          <Input
            placeholder="Search users..."
            defaultValue={userSearch}
            onChange={(e) => handleUserSearch(e.target.value)}
            className="text-sm mb-3"
          />
          <div className="space-y-3">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${String(selectedUser) === String(user.id)
                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : "hover-elevate text-muted-foreground"
                    }`}
                  onClick={() => onUserSelect(user.id)}
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-medium">{user.cn}</div>
                </div>
              ))
            ) : (
              <div className="py-8 text-sm text-center text-muted-foreground">No data found</div>
            )}
          </div>
        </div>
      )}

      {/* Privileges List */}
      <div className="flex-1 p-4 overflow-hidden bg-white border-r dark:bg-background">
        <h3 className="mb-4 text-sm font-medium">Privileges count : {priviledges.length || 0}</h3>
        <Input
          placeholder="Search privileges..."
          value={privilegeSearch}
          onChange={(e) => setPrivilegeSearch(e.target.value)}
          className="text-sm mb-3"
        />
        <div className="flex items-center gap-4 mb-2">
          <span className="text-sm font-medium text-muted-foreground">Privilege</span>
          <RadioGroup value={priviledgeFilter} onValueChange={setPriviledgeFilter} className="flex gap-4">
            {["ALL", "ALLOWED", "DENIED", "TIMED"].map((v) => (
              <div key={v} className="flex items-center space-x-2">
                <RadioGroupItem value={v} id={`filter-${v.toLowerCase()}`} />
                <Label htmlFor={`filter-${v.toLowerCase()}`} className="text-sm">{v}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-2">
          {
            loading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="p-3 border rounded-md">
                  <Skeleton className="w-3/4 h-4 mb-2" />
                  <Skeleton className="w-1/2 h-3" />
                </div>
              ))
            ) : selectedUser || selectedUser === "system" ? (
              filteredURL.length > 0 ? (
                filteredURL.map((privilege, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-md cursor-pointer transition-colors border ${selectedPrivilege?.cn === privilege.cn
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                      : "hover-elevate border-border"
                      }`}
                    onClick={() => setSelectedPrivilege(privilege)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center flex-1 min-w-0 gap-3">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded bg-muted">
                          <SettingsIcon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{privilege.cn}</div>
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0 gap-2">
                        <Badge
                          variant={
                            getStatus(privilege) === "ALLOWED"
                              ? "default"
                              : getStatus(privilege) === "DENIED"
                                ? "destructive"
                                : getStatus(privilege) === "TIMED"
                                  ? "secondary"
                                  : "default"
                          }
                          className="text-xs min-w-[60px] text-center"
                        >

                          {labelMapping(getPrivilegeStatus(privilege), getInheritedEndpointResourceStatusValueForProfilePrivilege(privilege))}
                        </Badge>
                        {labelMapping(getPrivilegeStatus(privilege), getInheritedEndpointResourceStatusValueForProfilePrivilege(privilege)) === "TIMED" && selectedUser !== "system" &&
                          timerData[privilege.cn] && (
                            <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-medium min-w-[50px]">
                              <Timer className="w-3 h-3" />
                              <span>{getTimerDisplay(privilege.cn)}</span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-sm text-center text-muted-foreground">No data found</div>
              )
            ) : (
              <div className="py-8 text-sm text-center text-muted-foreground">
                Select a user to view privileges
              </div>
            )}
        </div>
      </div>

      {/* Privilege Properties */}
      <div className="p-4 overflow-y-auto bg-white border-l w-80 dark:bg-background">
        {selectedPrivilege ? (
          <div className="space-y-6">
            <h3 className="mb-2 text-lg font-semibold">{selectedPrivilege.cn}</h3>
            <div>
              <label className="block mb-2 text-xs font-medium tracking-wide uppercase text-muted-foreground">
                Access Control {getPrivilegeStatus(selectedPrivilege)}
              </label>
              <Select
                value={getPrivilegeSelectStatusValue(selectedPrivilege)}
                onValueChange={(value) =>
                  handlePrivilegeStatusChange(selectedPrivilege.groupId, selectedPrivilege.profileId, value)
                }
              >

                <SelectTrigger className="h-auto min-h-8 text-xs w-55 border-blue-200 whitespace-normal break-words dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">

                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENABLED">ALLOWED</SelectItem>
                  <SelectItem value="DISABLED">DENIED</SelectItem>
                  <SelectItem value="DYNAMIC">TIMED</SelectItem>
                  {selectedUser !== "system" && (
                    <SelectItem value="INHERITED">INHERITED {getInheritedEndpointResourceStatusValueForProfilePrivilege(selectedPrivilege)}</SelectItem>
                  )}

                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-center text-muted-foreground">
              <div className="mb-2">⚙️</div>
              <div>No privilege selected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
